import * as React from 'react';

import { allowedImageFormats } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';

import { UIButton } from '~shared/components/buttons';
import { DialogConfirmation, DialogProps } from '~shared/components/Dialog';

import {
  InvalidImageSizeError,
  UnSupportedImageTypeError,
} from '../../../../api/images';

export interface GenericServerErrorDialogInnerProps
  extends Pick<DialogProps, 'open' | 'onClose'> {
  message?: string;
  title?: string;
}

/**
 * Generic server errors are for anything that does not return an axios response
 */
export function GenericServerErrorDialogInner({
  open,
  onClose,
  message = translate(
    "Sorry, something unexpected went wrong. Please refresh the page and try again. If problems persist, don’t hesitate to contact our <a class='link' href='https://help.socrative.com/'>support team</a>."
  ),
  title = translate('Unexpected Error'),
}: GenericServerErrorDialogInnerProps) {
  return (
    <DialogConfirmation
      id="server-error"
      open={open}
      title={title}
      onClose={onClose}
      closeLabel={translate('Close')}
      actions={[
        <UIButton key="ok" appearance="primary" onClick={onClose}>
          {translate('Ok', true)}
        </UIButton>,
      ]}
    >
      <p
        dangerouslySetInnerHTML={{
          __html: message,
        }}
      ></p>
    </DialogConfirmation>
  );
}

export interface GenericServerErrorDialogProps
  extends Pick<DialogProps, 'open' | 'onClose'> {
  error: unknown;
}

export function GenericServerErrorDialog({
  error,
  ...props
}: GenericServerErrorDialogProps) {
  if (error instanceof UnSupportedImageTypeError) {
    return (
      <GenericServerErrorDialogInner
        {...props}
        title={translate('Unsupported File Type')}
        message={translate('Try again with {0}').format(
          allowedImageFormats.map((v) => v.split('/')[1]).join(', ')
        )}
      />
    );
  }

  if (error instanceof InvalidImageSizeError) {
    return (
      <GenericServerErrorDialogInner
        {...props}
        title={translate('File Size Too Large')}
        message={translate('The maximum size is {0}.').format('10MB')}
      />
    );
  }

  return (
    <GenericServerErrorDialogInner
      message={(error as any)?.message}
      {...props}
    />
  );
}
